import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "./utils";
import { typography } from "./tokens";

export function Modal({
  children,
  onClose,
  className,
  overlayClassName,
  maxWidth = "max-w-lg",
  overlayZIndex = "z-[400]",
}) {
  const overlay = (
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center bg-black/40 p-4",
        overlayZIndex,
        overlayClassName
      )}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div className={cn("bg-white rounded-2xl shadow-xl w-full max-h-[90vh] flex flex-col", maxWidth, className)}>
        {children}
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return overlay;
  }

  return createPortal(overlay, document.body);
}

export function SidePanel({
  children,
  onClose,
  open = true,
  onClosed,
  className,
  overlayClassName,
  widthClassName = "max-w-3xl",
  overlayZIndex = "z-[400]",
  resizable = false,
  defaultWidth = 768,
  minWidth = 420,
}) {
  const [mounted, setMounted] = React.useState(open);
  const [show, setShow] = React.useState(false);
  const [width, setWidth] = React.useState(defaultWidth);
  const draggingRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(raf);
    }

    setShow(false);
    const timer = window.setTimeout(() => {
      setMounted(false);
      onClosed?.();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, onClosed]);

  React.useEffect(() => {
    if (open) setWidth(defaultWidth);
  }, [open, defaultWidth]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.body.style.userSelect = "none";
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const next = Math.min(window.innerWidth * 0.6, Math.max(minWidth, window.innerWidth - e.clientX));
    setWidth(next);
  };

  const handlePointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    document.body.style.userSelect = "";
  };

  if (!mounted) return null;

  const overlay = (
    <div
      className={cn(
        "fixed inset-0 bg-black/40 transition-opacity duration-300",
        show ? "opacity-100" : "opacity-0",
        overlayZIndex,
        overlayClassName
      )}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div
        className={cn(
          "absolute top-[67px] bottom-0 right-0 bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-out overflow-hidden",
          show ? "translate-x-0" : "translate-x-full",
          resizable ? "" : cn("w-full", widthClassName),
          className
        )}
        style={resizable ? { width, maxWidth: '60vw' } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {resizable && (
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            className="absolute left-0 top-0 h-full w-1.5 -translate-x-1/2 cursor-col-resize z-10 touch-none group"
          >
            <div className="mx-auto h-full w-px bg-gray-200 group-hover:bg-blue-400 group-active:bg-blue-500 transition-colors" />
          </div>
        )}
        {children}
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return overlay;
  }

  return createPortal(overlay, document.body);
}

export function ModalHeader({ title, onClose, children, className }) {
  return (
    <div className={cn("flex items-center justify-between px-6 py-4 border-b border-gray-100", className)}>
      {title ? (
        <h3 className="text-gray-900" style={typography.modalTitle}>{title}</h3>
      ) : children}
      {onClose && (
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function ModalBody({ children, className }) {
  return (
    <div className={cn("px-6 py-5 overflow-y-auto space-y-4", className)}>
      {children}
    </div>
  );
}

export function ModalFooter({ children, className }) {
  return (
    <div className={cn("px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3", className)}>
      {children}
    </div>
  );
}

export function ModalStepper({ steps, currentStep, className }) {
  return (
    <div className={cn("flex items-start justify-between px-6 mb-6", className)}>
      {steps.map((item, index, arr) => {
        const active = currentStep === item.id;
        const completed = currentStep > item.id;

        return (
          <div key={item.id} className="flex items-start flex-1">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold",
                  active && "bg-[#070719] text-white ring-4 ring-gray-200",
                  completed && "bg-[#070719] text-white",
                  !active && !completed && "bg-gray-100 text-gray-500"
                )}
              >
                {item.id}
              </div>

              <span
                className={cn(
                  "text-[10px] mt-2",
                  active ? "text-[#070719] font-semibold" : "text-gray-500"
                )}
              >
                {item.label}
              </span>
            </div>

            {index !== arr.length - 1 && (
              <div className="h-[2px] bg-gray-200 flex-1 mt-[18px] mx-3" />
            )}
          </div>
        );
      })}
    </div>
  );
}
